//utils/recipeUtils.jsx
/**
 * Generates a new unique ID for a recipe
 *
 * @param {Array} recipes - Array of existing recipes
 * @returns {number} - New unique ID
 */
export const generateNewId = (recipes) => {
  if (!Array.isArray(recipes) || recipes.length === 0) return 1;
  const ids = recipes.map((recipe) => Number(recipe.id)).filter((id) => Number.isInteger(id) && id > 0);
  return ids.length > 0 ? Math.max(...ids) + 1 : 1;
};

/**
 * Creates or updates a recipe with form values
 *
 * @param {Object} formValues - Values from the form
 * @param {boolean} isEditMode - Whether in edit mode
 * @param {Object} existingRecipe - Existing recipe if in edit mode
 * @param {Array} ingredients - Recipe ingredients
 * @param {Array} instructions - Recipe instructions
 * @param {Array} images - Recipe images
 * @param {Array} tags - Recipe tags
 * @returns {Object} - Updated recipe object
 */
export const createUpdatedRecipe = (formValues, isEditMode, existingRecipe, ingredients, instructions, images, tags) => {
  const { title = "", description = "", servings = 1, prepTime = 0, cookTime = 0, author = "" } = formValues;

  const totalTime = Number(prepTime) + Number(cookTime);

  const baseRecipe = {
    title,
    description,
    servings: Number(servings),
    prepTime: Number(prepTime),
    cookTime: Number(cookTime),
    totalTime,
    author,
    ingredients: ingredients || [],
    instructions: instructions || [],
    images: images || [],
    tags: tags || [],
  };

  const timestamp = new Date().toISOString();
  return isEditMode && existingRecipe ? { ...existingRecipe, ...baseRecipe, createdAt: timestamp, updatedAt: timestamp } : { ...baseRecipe, createdAt: timestamp, updatedAt: timestamp };
};

/**
 * Creates a copy of a recipe
 *
 * @param {Object} originalRecipe - Recipe to copy
 * @param {string} userId - ID of the user making the copy
 * @param {boolean} makePrivate - Whether to make the copy private
 * @returns {Object} - New recipe copy
 */
export const createRecipeCopy = (originalRecipe, userId, makePrivate = true) => {
  if (!originalRecipe || !userId) return null;
  return {
    ...originalRecipe,
    createdBy: userId,
    isPublic: !makePrivate && originalRecipe.isPublic,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    copiedFrom: originalRecipe.id,
  };
};

/**
 * Formats minutes into a string with hours and minutes
 *
 * @param {number} minutes - Time in minutes
 * @param {Function} t - Translation function to retrieve localized strings
 * @returns {string} - Formatted time (e.g. "8 t 20 min")
 */
export const formatTime = (minutes, t) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  let formattedTime = "";
  if (hours > 0) {
    formattedTime += `${hours} ${t("hours")}`;
  }
  if (remainingMinutes > 0) {
    formattedTime += (hours > 0 ? " " : "") + `${remainingMinutes} ${t("minutes")}`;
  }
  return formattedTime || `0 ${t("minutes")}`;
};
